import React from 'react';
import * as R from 'ramda';
import {
  isChild as userIsChild,
  isTeen as userIsTeen,
} from '@practicegenius/domain/src/data/users/birthdate';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';
import {
  calculateProgress,
  primaryActionButtonProps,
  PrimaryActionButtonProps,
  secondaryActionButtonProps,
  SecondaryActionButtonProps,
} from './utils';
import {Event, State} from '../machines/machineTypes';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.registerPhoneNumber;
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  title: string;
  instructions: string;
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
  phoneInputProps: {
    label: string;
    value: string | null;
    onChange: (value: string) => unknown;
    onBlur: () => unknown;
    error?: string;
    helperText: string;
  };
  alternativePhonePrompt?: {
    label: string;
    linkText: string;
    onClick: () => unknown;
  };
  // Country code of practice user, if present
  userCountryCode?: string;
};

export type RegisterPhoneNumberScreenType = React.ComponentType<ScreenProps>;

const screenLang = lang.registerPhoneNumber;
const perspectives = {
  minor: {
    field: 'responsiblePhone' as const,
    label: screenLang.phoneInput.label.minor,
    title: screenLang.title.minor,
    alternativePhonePrompt: {
      label: screenLang.alternativePhonePrompt.label.notMinor,
      linkText: screenLang.alternativePhonePrompt.linkText.notMinor,
    },
  },
  notMinor: {
    field: 'phone' as const,
    label: screenLang.phoneInput.label.notMinor,
    title: screenLang.title.notMinor,
    alternativePhonePrompt: {
      label: screenLang.alternativePhonePrompt.label.minor,
      linkText: screenLang.alternativePhonePrompt.linkText.minor,
    },
  },
};

export const RegisterPhoneNumber: React.FC<
  Props & {
    children: RegisterPhoneNumberScreenType;
  }
> = ({registrationMachine, children: Screen}) => {
  const [{context, matches}, send] = registrationMachine;

  const {birthdate} = context.account;
  const isChild = birthdate ? userIsChild(birthdate as Date) : false;
  const isTeen = birthdate ? userIsTeen(birthdate as Date) : false;

  const [useResponsiblePhone, setUseResponsiblePhone] = React.useState(isChild);
  const {field, label, title, alternativePhonePrompt} =
    perspectives[useResponsiblePhone ? 'minor' : 'notMinor'];
  const {field: otherField} =
    perspectives[useResponsiblePhone ? 'notMinor' : 'minor'];

  const progress = calculateProgress({
    draftAccount: context.account,
    location: context.navigator.location,
  });

  const phone = context.account[field] ?? context.practiceUserRecord[field];

  const actionButtons = {
    primary: primaryActionButtonProps({
      children: screenLang.buttons.primary.title,
      loading: matches({
        [State.phoneNumber]: State.submittingPhoneNumber,
      }),
      disabled: Boolean(!phone || context.validationErrors?.account?.[field]),
    }),
    secondary: secondaryActionButtonProps({
      children: screenLang.buttons.secondary.title,
      onClick: () => context.navigator.goBack(),
      loading: false,
      disabled: false,
    }),
  };

  const renderProps: ScreenProps = {
    registrationMachine,
    lang: screenLang,
    onFormSubmit: e => {
      e.preventDefault();
      send({
        type: Event.UPDATE_REGISTRATION,
        payload: {
          [field]: phone,
          [otherField]: null,
          useResponsiblePhone,
        },
      });
    },
    title,
    instructions: screenLang.instructions,
    progress,
    actionButtons,
    phoneInputProps: {
      label,
      value: phone,
      onChange: val => {
        send({
          type: Event.UPDATE_DATA,
          payload: {account: {[field]: val}},
        });
      },
      onBlur: () => {
        if (!phone) {
          send({
            type: Event.HAS_VALIDATION_ERRORS,
            errors: {
              account: {
                [field]: screenLang.phoneInput.errors.blank,
              },
            },
          });
        }
      },
      error: context.validationErrors?.account?.[field],
      helperText: screenLang.phoneInput.helperText,
    },
    alternativePhonePrompt: !isTeen
      ? undefined
      : {
          ...alternativePhonePrompt,
          onClick: () => setUseResponsiblePhone(R.not),
        },
    userCountryCode: context.practiceUserRecord.country ?? undefined,
  };

  return <Screen {...renderProps} />;
};
